/**
 * THE LIBRARY CONNECTOR VOCABULARY — declared once.
 *
 * Library LB-6b (decision 33, the sync schedule; principles.md #1 and #3).
 *
 * WHAT THIS FILE IS THE ONLY DECLARATION OF
 *   - the sync-schedule alphabet a source may be set to. It is the set the
 *     `library_connectors.sync_schedule` CHECK (migration b52) admits. Before
 *     LB6b-T0 it was written out twice — in the api's Drizzle schema module
 *     and in `packages/connectors/src/shared.ts` — "kept in sync by hand".
 *     Both copies are gone; the api route that validates a schedule and every
 *     later reader (the worker's due rule, LB6b-T2) import it from here, and
 *     nothing re-exports it.
 *
 * WHAT IT DOES NOT DECIDE. When a source is due is THE due rule in the api
 * host (`listDueConnectors`, LB6b-T2); this file names the values only.
 */

import { z } from 'zod';

/**
 * Every schedule a source may carry. `manual_only` and `webhook_only` never
 * run on a timer; the rest name an interval.
 */
export const LIBRARY_CONNECTOR_SYNC_SCHEDULES = Object.freeze([
  'every_5min',
  'every_15min',
  'every_1h',
  'every_6h',
  'daily',
  'manual_only',
  'webhook_only',
] as const);
export const LibraryConnectorSyncScheduleSchema = z.enum(LIBRARY_CONNECTOR_SYNC_SCHEDULES);
export type LibraryConnectorSyncSchedule = z.infer<typeof LibraryConnectorSyncScheduleSchema>;

/** The schedules that run on a timer — every one but `manual_only` and `webhook_only`. */
export type LibraryConnectorTimedSchedule = Exclude<LibraryConnectorSyncSchedule, 'manual_only' | 'webhook_only'>;

/**
 * How often each timed schedule runs, in minutes (decision 33). The due rule
 * (`listDueConnectors`, the api host) reads this map and nothing else: a
 * source on a schedule NOT in this map never runs on a timer. `every_5min`
 * stays (ruling OQ-L6b-6: a shipped option; removing it is the founder's
 * call).
 */
export const LIBRARY_CONNECTOR_SYNC_INTERVAL_MINUTES: Readonly<Record<LibraryConnectorTimedSchedule, number>> =
  Object.freeze({
    every_5min: 5,
    every_15min: 15,
    every_1h: 60,
    every_6h: 360,
    daily: 1440,
  });

/**
 * Why a source is paused (migration c47's CHECK admits exactly these;
 * founder decision OQ-L6b-1, ruling OQ-L6b-2):
 *   - `credentials` — its sign-in failed (the provider refused the token, a
 *     refresh failed, the stored credential is missing or unreadable). Paused
 *     at once. Cleared ONLY by a successful re-authentication; no person can
 *     clear it by hand, and Sync now answers 409 while it stands.
 *   - `failures` — LIBRARY_CONNECTOR_FAILURES_TO_PAUSE failed runs in a row,
 *     of any cause. Cleared by a run that succeeds (Sync now is the person's
 *     retry) or by a re-authentication.
 * A person's own "Sync paused" is NOT a pause reason: it is `sync_enabled`.
 */
export const LIBRARY_CONNECTOR_PAUSE_REASONS = Object.freeze(['credentials', 'failures'] as const);
export const LibraryConnectorPauseReasonSchema = z.enum(LIBRARY_CONNECTOR_PAUSE_REASONS);
export type LibraryConnectorPauseReason = z.infer<typeof LibraryConnectorPauseReasonSchema>;

/** Failed runs in a row, of any cause, after which a source pauses (founder decision OQ-L6b-1). */
export const LIBRARY_CONNECTOR_FAILURES_TO_PAUSE = 5;

/**
 * The named system actor a SCHEDULED sync acts as (founder decision
 * OQ-L6b-4): its audit rows name it — never the source's creator, who may be
 * gone — inside the run's tenant. A Sync now records the person who clicked.
 *
 * `id` is the actor's reserved identity, written as `ai_documents.uploaded_by`
 * of what a scheduled run syncs (OQ-L6b-4: "audit rows and uploaded_by";
 * LB6bT3-REV-02). It is no users row (the column has no foreign key), so it
 * never matches a person — it grants no uploader standing to anyone — and the
 * document's own tenant scopes it, making it the actor of THAT tenant. Readers
 * that name an uploader answer `name` for it.
 */
export const LIBRARY_SCHEDULED_SYNC_ACTOR = Object.freeze({
  id: 'a2905000-5c4e-4d00-8000-0000000000a1',
  key: 'library.connector.scheduled_sync',
  name: 'Argus (scheduled sync)',
} as const);
